import {
  apiGetOpportunityCategories,
  apiGetOpportunities,
  apiCreateOpportunity,
  apiUpdateOpportunity,
  apiDeleteOpportunity,
} from "@api/opportunity";
import type { CreateOpportunityPayload, UpdateOpportunityPayload } from "@api/opportunity";
import {
  setOpportunityCategories,
  setOpportunityCategoriesLoading,
  setOpportunities,
  setOpportunitiesLoading,
  setOpportunityError,
} from "@app-redux/slices/opportunitySlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "@app-redux/store";
import { AxiosError } from "axios";

const friendlyMessage = (e: unknown, fallback: string): string => {
  if (e instanceof AxiosError) {
    const status = e.response?.status;
    const serverMsg = e.response?.data?.message || e.response?.data?.title;
    if (serverMsg) return serverMsg;
    if (status === 400) return "The server couldn't process the request. Please check your input and try again.";
    if (status === 403) return "You don't have permission to perform this action.";
    if (status === 404) return "The requested record was not found. It may have been deleted.";
    if (status === 500) return "An unexpected server error occurred. Please try again later.";
    if (!e.response) return "Unable to reach the server. Please check your connection.";
  }
  return fallback;
};

export const getOpportunityCategories = createAsyncThunk(
  "opportunity/getCategories",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setOpportunityCategoriesLoading());
      const data = await apiGetOpportunityCategories();
      thunkAPI.dispatch(setOpportunityCategories(data));
      return data;
    } catch (e: unknown) {
      const msg = friendlyMessage(e, "Unable to load categories. Please try again.");
      thunkAPI.dispatch(setOpportunityError(msg));
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const getOpportunities = createAsyncThunk(
  "opportunity/getOpportunities",
  async (categoryId: string, thunkAPI) => {
    try {
      thunkAPI.dispatch(setOpportunitiesLoading());
      const data = await apiGetOpportunities(categoryId);
      thunkAPI.dispatch(setOpportunities(data));
      return data;
    } catch (e: unknown) {
      const msg = friendlyMessage(e, "Unable to load opportunities. Please try again.");
      thunkAPI.dispatch(setOpportunityError(msg));
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const createOpportunity = createAsyncThunk(
  "opportunity/create",
  async (payload: CreateOpportunityPayload, thunkAPI) => {
    try {
      const result = await apiCreateOpportunity(payload);
      const state = thunkAPI.getState() as RootState;
      const categoryId = state.opportunity.selectedCategoryId;
      if (categoryId) {
        thunkAPI.dispatch(getOpportunities(categoryId));
      }
      return result;
    } catch (e: unknown) {
      const msg = friendlyMessage(e, "Unable to create the opportunity. Please try again.");
      thunkAPI.dispatch(setOpportunityError(msg));
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const updateOpportunity = createAsyncThunk(
  "opportunity/update",
  async ({ id, payload }: { id: string; payload: UpdateOpportunityPayload }, thunkAPI) => {
    try {
      await apiUpdateOpportunity(id, payload);
      const state = thunkAPI.getState() as RootState;
      const categoryId = state.opportunity.selectedCategoryId;
      if (categoryId) {
        thunkAPI.dispatch(getOpportunities(categoryId));
      }
      return;
    } catch (e: unknown) {
      const msg = friendlyMessage(e, "Unable to save changes. Please try again.");
      thunkAPI.dispatch(setOpportunityError(msg));
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const deleteOpportunity = createAsyncThunk(
  "opportunity/delete",
  async (id: string, thunkAPI) => {
    try {
      await apiDeleteOpportunity(id);
      const state = thunkAPI.getState() as RootState;
      const categoryId = state.opportunity.selectedCategoryId;
      if (categoryId) {
        thunkAPI.dispatch(getOpportunities(categoryId));
      }
      return;
    } catch (e: unknown) {
      const msg = friendlyMessage(e, "Unable to delete the opportunity. Please try again.");
      thunkAPI.dispatch(setOpportunityError(msg));
      return thunkAPI.rejectWithValue(msg);
    }
  }
);
